import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { blogPosts } from './blogPostsData';
import HeroSection from './HeroSection';
import JoinTheClub from '../HomePage/JoinTheClub';
import LazyImage from '../../components/LazyImage';
import BlogPostNotFound from './BlogPostNotFound';

const BlogPostDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <BlogPostNotFound />;
  }

  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      
      {/* Main Article Content */}
      <article className="mx-auto px-4 md:px-10 lg:px-32 w-full pt-20">
        {/* Title Section */}
        <div className="mb-8">
          <div className="mb-6">
            <LazyImage 
              src={post.flagImage}
              alt={post.title}
              className="w-full h-auto object-contain rounded-t-[22px]"
              sizes="100vw"
              priority={true}
              backgroundColor="transparent"
            />
          </div>
        </div>

        {/* Introduction */}
        {post.introduction && (
          <div className="mb-12">
            {post.introduction.map((paragraph, index) => {
              // Add space after "Record highs blind..." (index 1), "Here at The Wealthy Miner..." (index 2) and "That hasn't changed..." (index 3)
              const shouldAddSpace = index === 1 || index === 2 || index === 3;
              return (
                <p 
                  key={index}
                  className={`text-[16px] md:text-[20px] lg:text-[20px] text-[#000] leading-[29px] font-[400] ${shouldAddSpace ? 'mb-6' : ''} text-justify`}
                  dangerouslySetInnerHTML={{ __html: paragraph }}
                />
              );
            })}
          </div>
        )}

      </article>

      {/* Red Flags Sections - Full Width */}
      {post.redFlags && post.redFlags.map((flag, index) => {
        const isOddFlag = flag.number === 1 || flag.number === 3 || flag.number === 5;
        const bgColor = isOddFlag ? 'bg-[#F6F6F6]' : 'bg-[#FFFFFF]';
        const isLastFlag = index === post.redFlags!.length - 1;
        const roundedClass = isOddFlag ? 'rounded-l-[22px]' : 'rounded-r-[22px]';
        
        return (
          <section key={flag.number} className={`w-full ${bgColor} py-8 md:py-12`}>
            <div className="mx-auto px-4 md:px-10 lg:px-32 w-full">
              <div className={`flex flex-col ${flag.imagePosition === 'right' ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-stretch`}>
                {/* Content */}
                <div className="w-full lg:w-1/2 flex flex-col">
                  <div className="mb-4">
                    <p className="font-[400] text-[#000] leading-[40px] text-[30px] md:text-[40px] lg:text-[32px]">
                      Red Flag #{flag.number}:
                    </p>
                    <h2 className="font-[700] text-[#000] mb-2 lg:leading-[40px] leading-[30px] text-[30px] md:text-[40px] lg:text-[32px]">
                      {flag.title}
                    </h2>
                  </div>
                  <p 
                    className="text-[16px] md:text-[20px] lg:text-[20px] text-[#000] leading-[29px] font-[400] mb-4 last:mb-0 text-justify flex-1"
                    dangerouslySetInnerHTML={{ __html: flag.content }}
                  />
                </div>

                {/* Image */}
                <div className="w-full lg:w-1/2 flex-shrink-0 flex flex-col">
                  <div className={`relative w-full h-full min-h-[300px] md:min-h-[400px] overflow-hidden ${roundedClass}`}>
                    <LazyImage 
                      src={flag.image}
                      alt={flag.title}
                      className="w-full h-full object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </div>
                </div>
              </div> 
              {/* Author Signature - only in last flag, under the image */}
                  {isLastFlag && post.authorSignature && (
                    <div className="lg:mt-14 mt-8">
                      <p className="flex flex-col gap-0">
                        <span className="font-[400] text-[#000] leading-normal text-[15px] md:text-[18px] lg:text-[20px]">Good Investing,</span>
                        <span className="font-[700] text-[#000] leading-normal text-[18px] md:text-[20px] lg:text-[25px] -mt-1">Rob Bruggeman, CFA</span>
                      </p>
                    </div>
                  )}
            </div>
          </section>
        );
      })}

      {/* Content Sections - For regular blog posts */}
      {post.contentSections && post.contentSections.map((section, index) => {
        const isLastSection = index === post.contentSections!.length - 1;
        const bgColor = index % 2 === 0 ? 'bg-[#F6F6F6]' : 'bg-white';
        const hasImage = section.image && section.imagePosition;
        
        // Component to handle height matching
        const ContentSectionWithImage: React.FC = () => {
          const textRef = useRef<HTMLDivElement>(null);
          const imageRef = useRef<HTMLDivElement>(null);
          const [imageHeight, setImageHeight] = useState<number | null>(null);

          useEffect(() => {
            const updateHeight = () => {
              if (textRef.current && imageRef.current && window.innerWidth >= 1024) {
                const height = textRef.current.offsetHeight;
                setImageHeight(height);
              } else {
                setImageHeight(null);
              }
            };

            updateHeight();
            window.addEventListener('resize', updateHeight);
            
            // Also update after images load
            const images = document.querySelectorAll('img');
            images.forEach(img => {
              img.addEventListener('load', updateHeight);
            });

            return () => {
              window.removeEventListener('resize', updateHeight);
              images.forEach(img => {
                img.removeEventListener('load', updateHeight);
              });
            };
          }, []);

          return (
            <div className={hasImage ? `grid grid-cols-1 lg:grid-cols-2 gap-8 items-start ${section.imagePosition === 'right' ? '' : 'lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1'}` : ''}>
              {/* Content */}
              <div ref={textRef} className={hasImage ? "w-full flex flex-col" : "w-full flex flex-col"}>
                <h2 className="font-[700] text-[#000] mb-6 lg:leading-[40px] leading-[30px] text-[30px] md:text-[40px] lg:text-[32px]">
                  {section.title}
                </h2>
                <div className="flex flex-col">
                  {section.content.map((paragraph, pIndex) => {
                    // Add space after "They get executed if a position drops by a pre-set percentage."
                    const shouldAddSpace0 = paragraph.includes("They get executed if a position drops by a pre-set percentage.");
                    // Add space after "This is key. And this could change your investment game. Here's an example:"
                    const shouldAddSpace1 = paragraph.includes("This is key. And") && paragraph.includes("this could change your investment game") && paragraph.includes("Here's an example:");
                    // Add space after the long paragraph about 20% stop loss
                    const shouldAddSpace2 = paragraph.includes("But if you set up your stop loss at 20%, for example, then your brokerage will track the share price and sell if it falls by that percentage");
                    // Add space after "The exact percentage for these orders will depend on the kind of investment..."
                    const shouldAddSpace5 = paragraph.includes("The exact percentage for these orders will depend on the kind of investment") && paragraph.includes("So setting your trailing stop at 20–25% makes sense.");
                    // Add space after paragraph ending with "nature of the investment."
                    const shouldAddSpace3 = paragraph.includes("but just because of the nature of the investment.");
                    // Add space after paragraph with "One exception to this rule"
                    const shouldAddSpace4 = paragraph.includes("<strong>One exception to this rule</strong>");
                    const shouldAddSpace = shouldAddSpace0 || shouldAddSpace1 || shouldAddSpace2 || shouldAddSpace3 || shouldAddSpace4 || shouldAddSpace5;
                    const isLastParagraph = pIndex === section.content.length - 1;
                    return (
                      <p 
                        key={pIndex}
                        className={`text-[16px] md:text-[20px] lg:text-[20px] text-[#000] leading-[29px] font-[400] ${shouldAddSpace && !isLastParagraph ? 'mb-6' : ''} text-justify`}
                        dangerouslySetInnerHTML={{ __html: paragraph }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Image - only render if image exists */}
              {hasImage && (
                <div 
                  ref={imageRef}
                  className="w-full flex flex-col"
                  style={imageHeight ? { height: `${imageHeight}px` } : undefined}
                >
                  <div className={`relative w-full h-full overflow-hidden ${section.imagePosition === 'right' ? 'rounded-r-[22px]' : 'rounded-l-[22px]'}`}>
                    <LazyImage 
                      src={section.image!}
                      alt={section.title}
                      className="w-full h-full object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        };
        
        return (
          <section key={index} className={`w-full ${bgColor} py-8 md:py-12`}>
            <div className="mx-auto px-4 md:px-10 lg:px-32 w-full">
              {hasImage ? <ContentSectionWithImage /> : (
                <div className="w-full flex flex-col">
                  <h2 className="font-[700] text-[#000] mb-6 lg:leading-[40px] leading-[30px] text-[30px] md:text-[40px] lg:text-[32px]">
                    {section.title}
                  </h2>
                  <div className="flex flex-col">
                    {section.content.map((paragraph, pIndex) => {
                      const shouldAddSpace0 = paragraph.includes("They get executed if a position drops by a pre-set percentage.");
                      const shouldAddSpace1 = paragraph.includes("This is key. And") && paragraph.includes("this could change your investment game") && paragraph.includes("Here's an example:");
                      const shouldAddSpace2 = paragraph.includes("But if you set up your stop loss at 20%, for example, then your brokerage will track the share price and sell if it falls by that percentage");
                      const shouldAddSpace5 = paragraph.includes("The exact percentage for these orders will depend on the kind of investment") && paragraph.includes("So setting your trailing stop at 20–25% makes sense.");
                      const shouldAddSpace3 = paragraph.includes("but just because of the nature of the investment.");
                      const shouldAddSpace4 = paragraph.includes("<strong>One exception to this rule</strong>");
                      const shouldAddSpace = shouldAddSpace0 || shouldAddSpace1 || shouldAddSpace2 || shouldAddSpace3 || shouldAddSpace4 || shouldAddSpace5;
                      const isLastParagraph = pIndex === section.content.length - 1;
                      return (
                        <p 
                          key={pIndex}
                          className={`text-[16px] md:text-[20px] lg:text-[20px] text-[#000] leading-[29px] font-[400] ${shouldAddSpace && !isLastParagraph ? 'mb-6' : ''} text-justify`}
                          dangerouslySetInnerHTML={{ __html: paragraph }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Author Signature - only in last section */}
              {isLastSection && post.authorSignature && (
                <div className="lg:mt-14 mt-8">
                  <p className="flex flex-col gap-0">
                    {post.authorSignature.split('\n').map((line, i) => (
                      <span 
                        key={i}
                        className={`text-[#000] leading-normal text-[15px] md:text-[18px] lg:text-[20px] ${i === 0 ? 'font-[400]' : 'font-[700] text-[18px] md:text-[20px] lg:text-[25px] -mt-1'}`}
                      >
                        {line}
                      </span>
                    ))}
                  </p>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* Join The Club Section */}
      <JoinTheClub />
    </div>
  );
};

export default BlogPostDetail;
